/**
 * Projects API client.
 *
 * One thin wrapper per backend route. Every mutation that changes membership
 * returns the FULL refreshed member board, so callers never have to re-fetch to
 * stay consistent — that round trip is where a members board drifts out of sync
 * with the server in a multi-user feature.
 */

import api from '@services/api';
import type {
  AppNotification,
  InviteResponse,
  MemberRole,
  ProjectAgent,
  ProjectConversation,
  ProjectConversationFilters,
  ProjectFile,
  ProjectFilesResponse,
  ProjectLibraryLink,
  ProjectMemberRow,
  ProjectSummary,
  ShareTargets,
} from '../types';

const BASE = '/projects';
const NOTIFICATIONS = '/notifications';

export interface CreateProjectPayload {
  project_name: string;
  project_description?: string;
  project_context_prompt?: string;
  project_color?: string;
  project_icon?: string;
}

export type UpdateProjectPayload = Partial<CreateProjectPayload>;

export interface InvitePayload {
  user_ids?: string[];
  tenant_ids?: string[];
  invite_message?: string;
}

export const projectService = {
  // ── Projects ───────────────────────────────────────────────────────────
  async listProjects(includeArchived = false): Promise<ProjectSummary[]> {
    const response = await api.get(BASE, {
      params: includeArchived ? { include_archived: 'true' } : undefined,
    });
    return response.data.projects || [];
  },

  async getProject(projectId: string): Promise<ProjectSummary> {
    const response = await api.get(`${BASE}/${projectId}`);
    return response.data.project;
  },

  async createProject(payload: CreateProjectPayload): Promise<ProjectSummary> {
    const response = await api.post(BASE, payload);
    return response.data.project;
  },

  async updateProject(projectId: string, payload: UpdateProjectPayload): Promise<ProjectSummary> {
    const response = await api.patch(`${BASE}/${projectId}`, payload);
    return response.data.project;
  },

  async archiveProject(projectId: string): Promise<ProjectSummary> {
    const response = await api.post(`${BASE}/${projectId}/archive`);
    return response.data.project;
  },

  async reopenProject(projectId: string): Promise<ProjectSummary> {
    const response = await api.post(`${BASE}/${projectId}/reopen`);
    return response.data.project;
  },

  /** Permanent. The name is echoed back and verified server-side, not just here. */
  async deleteProject(projectId: string, confirmName: string): Promise<void> {
    await api.delete(`${BASE}/${projectId}`, { data: { confirm_name: confirmName } });
  },

  // ── Members ────────────────────────────────────────────────────────────
  /** Walk away from a project. Only the caller's own membership. */
  async leaveProject(projectId: string): Promise<void> {
    await api.post(`${BASE}/${projectId}/leave`);
  },

  async listMembers(projectId: string): Promise<ProjectMemberRow[]> {
    const response = await api.get(`${BASE}/${projectId}/members`);
    return response.data.members || [];
  },

  async invite(
    projectId: string,
    payload: InvitePayload,
  ): Promise<{ result: InviteResponse; members: ProjectMemberRow[] }> {
    const response = await api.post(`${BASE}/${projectId}/members`, payload);
    return { result: response.data.result, members: response.data.members || [] };
  },

  async removeMember(projectId: string, memberId: string): Promise<ProjectMemberRow[]> {
    const response = await api.delete(`${BASE}/${projectId}/members/${memberId}`);
    return response.data.members || [];
  },

  async setMemberRole(
    projectId: string,
    memberId: string,
    role: MemberRole,
  ): Promise<ProjectMemberRow[]> {
    const response = await api.post(`${BASE}/${projectId}/members/${memberId}/role`, {
      member_role: role,
    });
    return response.data.members || [];
  },

  async resendInvitation(projectId: string, memberId: string): Promise<ProjectMemberRow[]> {
    const response = await api.post(`${BASE}/${projectId}/members/${memberId}/resend`);
    return response.data.members || [];
  },

  async resendAllInvitations(
    projectId: string,
  ): Promise<{ resentCount: number; members: ProjectMemberRow[] }> {
    const response = await api.post(`${BASE}/${projectId}/members/resend-all`);
    return {
      resentCount: response.data.resent_count || 0,
      members: response.data.members || [],
    };
  },

  async getShareTargets(projectId: string): Promise<ShareTargets> {
    const response = await api.get(`${BASE}/${projectId}/share-targets`);
    return {
      users: response.data.users || [],
      tenants: response.data.tenants || [],
      can_share_with_tenants: !!response.data.can_share_with_tenants,
    };
  },

  // ── Invitations (answered by the invitee, not by a manager) ────────────
  async acceptInvitation(memberId: string): Promise<void> {
    await api.post(`${BASE}/invitations/${memberId}/accept`);
  },

  async declineInvitation(memberId: string): Promise<void> {
    await api.post(`${BASE}/invitations/${memberId}/decline`);
  },

  // ── Conversations ──────────────────────────────────────────────────────
  async listConversations(
    projectId: string,
    filters: ProjectConversationFilters = {},
  ): Promise<ProjectConversation[]> {
    const params: Record<string, string> = {};
    if (filters.contributor_id) params.contributor_id = filters.contributor_id;
    if (filters.chatbot_id) params.chatbot_id = filters.chatbot_id;
    if (filters.created_by) params.created_by = filters.created_by;
    const response = await api.get(`${BASE}/${projectId}/conversations`, { params });
    return response.data.conversations || [];
  },

  /** Agents already used in this project — drives the picker's consent step. */
  async listAgents(projectId: string): Promise<ProjectAgent[]> {
    const response = await api.get(`${BASE}/${projectId}/agents`);
    return response.data.agents || [];
  },

  // ── Files (the project's own data bundle) ──────────────────────────────
  async listFiles(projectId: string): Promise<ProjectFilesResponse> {
    const response = await api.get(`${BASE}/${projectId}/files`);
    return {
      files: response.data.files || [],
      bundle_missing: !!response.data.bundle_missing,
      data_bundle_id: response.data.data_bundle_id,
    };
  },

  async uploadFile(projectId: string, file: File): Promise<ProjectFile> {
    const formData = new FormData();
    formData.append('file', file);
    const response = await api.post(`${BASE}/${projectId}/files/upload`, formData, {
      headers: { 'Content-Type': 'multipart/form-data' },
      // Uploads are chunked and embedded server-side; the default 30s ceiling
      // fails a perfectly good upload of a large document.
      timeout: 180000,
    });
    return response.data.file;
  },

  async removeFile(projectId: string, fileId: string): Promise<void> {
    await api.delete(`${BASE}/${projectId}/files/${fileId}`);
  },

  // ── Library links (vault folders/files shared into the project) ────────
  async listLibraryLinks(projectId: string): Promise<ProjectLibraryLink[]> {
    const response = await api.get(`${BASE}/${projectId}/library-links`);
    return response.data.links || [];
  },

  async createLibraryLink(
    projectId: string,
    payload: { folder_id?: string; file_id?: string; include_as_context?: boolean },
  ): Promise<ProjectLibraryLink> {
    const response = await api.post(`${BASE}/${projectId}/library-links`, payload);
    return response.data.link;
  },

  async removeLibraryLink(projectId: string, linkId: string): Promise<void> {
    await api.delete(`${BASE}/${projectId}/library-links/${linkId}`);
  },
  // ── Notifications ──────────────────────────────────────────────────────
  // The bell's own API (/notifications): project invitations AND ready
  // Insights reports, newest first. Projects' invitations read as before.
  /** One page of notifications, newest first. */
  async listNotifications(
    limit = 10,
    offset = 0,
  ): Promise<{ notifications: AppNotification[]; hasMore: boolean }> {
    const response = await api.get(NOTIFICATIONS, { params: { limit, offset } });
    return {
      notifications: response.data.notifications || [],
      hasMore: !!response.data.has_more,
    };
  },

  async unreadCount(): Promise<number> {
    const response = await api.get(`${NOTIFICATIONS}/unread-count`);
    return response.data.unread_count || 0;
  },

  async markRead(notificationId: string): Promise<void> {
    await api.post(`${NOTIFICATIONS}/${notificationId}/read`);
  },

  async markAllRead(): Promise<void> {
    await api.post(`${NOTIFICATIONS}/read-all`);
  },
};

export default projectService;
